import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { ToolHeader, RateLimited } from "@/pages/tools/CatalogAnalyzer";
import AnalysisProgress from "@/components/AnalysisProgress";
import CopyBlock from "@/components/CopyBlock";
import { Copy, ArrowRight } from "@phosphor-icons/react";

function Label({ children }) { return <label className="label-mono mb-1.5 block">{children}</label>; }

export default function ListingOptimizer() {
  const [title, setTitle] = useState("");
  const [bullets, setBullets] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);

  useEffect(() => { api.get("/tools/usage").then((r) => setUsage(r.data)); }, []);

  const submit = async () => {
    const bList = bullets.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!title.trim() || bList.length === 0) {
      toast.error("Provide a title and at least one bullet");
      return;
    }
    setLoading(true); setError(""); setResult(null); setRateLimited(false);
    try {
      const { data } = await api.post("/tools/listing-optimizer", { title, bullets: bList });
      setResult(data.result);
      const u = await api.get("/tools/usage"); setUsage(u.data);
    } catch (e) {
      if (e?.response?.status === 429) { setRateLimited(true); setError(apiError(e)); }
      else setError(apiError(e));
    } finally { setLoading(false); }
  };

  const u = usage?.tools?.["listing-optimizer"];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <ToolHeader no="02" title="Listing Optimizer"
        subtitle="Paste your current title and bullets. We rewrite them keyword-rich, conversion-tuned, with the reason for every change."
        used={u?.used ?? 0} remaining={u?.remaining ?? 5} limit={usage?.limit ?? 5} isAdmin={usage?.is_admin} />

      <div className="mt-10 grid md:grid-cols-2 gap-px bg-rule border border-rule">
        <div className="bg-white p-6 md:p-8 space-y-5">
          <div className="label-mono">Input · Current listing</div>
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="lo-title" />
          </div>
          <div>
            <Label>Bullets · one per line</Label>
            <Textarea rows={8} value={bullets} onChange={(e) => setBullets(e.target.value)} data-testid="lo-bullets" />
          </div>
          <Button onClick={submit} disabled={loading} data-testid="lo-submit">
            {loading ? "Rewriting…" : <>Optimize <ArrowRight className="w-4 h-4 ml-2" weight="bold" /></>}
          </Button>
          {error && !rateLimited && <div className="text-sm text-alert border border-alert/30 bg-alert-bg px-3 py-2">{error}</div>}
          <AnalysisProgress
            active={loading}
            done={!!result}
            label="Listing rewrite"
            stages={[
              { title: "Parsing current title + bullets", ms: 1200 },
              { title: "Researching ranked keywords", ms: 2200 },
              { title: "Rewriting for A9 + AI search", ms: 3500 },
              { title: "Generating backend search terms + A+ copy", ms: 2200 },
            ]}
          />
        </div>

        <div className="bg-white p-6 md:p-8" data-testid="lo-result">
          <div className="label-mono mb-5">Output · Rewritten listing · copy-paste ready</div>
          {!result ? (
            <div className="text-ink/40 text-sm h-full grid place-items-center min-h-[300px] border border-dashed border-rule">
              Your rewritten listing will appear here.
            </div>
          ) : (
            <div className="space-y-4">
              {result.optimizedTitle && (
                <CopyBlock title="Product title" value={result.optimizedTitle}
                  hint="Paste into Item name" charLimit={200} testid="lo-copy-title" />
              )}
              {(result.bullets || []).length > 0 && (
                <CopyBlock title="Key product features · 5 bullets"
                  value={(result.bullets || []).map((b) => b.text)}
                  hint="Paste into Key product features" testid="lo-copy-bullets" />
              )}
              {/* Show rationale inline below the bullets block for clarity */}
              {(result.bullets || []).some((b) => b.rationale) && (
                <details className="border border-rule bg-paper">
                  <summary className="px-4 py-2 label-mono cursor-pointer hover:bg-surface-hover">
                    Why these changes · rationale per bullet
                  </summary>
                  <ol className="divide-y divide-rule">
                    {(result.bullets || []).map((b, i) => (
                      <li key={`r-${i}`} className="px-4 py-3 flex gap-3">
                        <span className="font-mono tabular text-ink/40 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-xs text-ink/70 leading-relaxed">{b.rationale}</span>
                      </li>
                    ))}
                  </ol>
                </details>
              )}
              {result.description && (
                <CopyBlock title="Product description" value={result.description}
                  hint="Paste into Product description" testid="lo-copy-description" />
              )}
              {result.search_terms && (
                <CopyBlock title="Backend search terms" value={result.search_terms}
                  hint="Paste into Keywords → Search terms (Amazon limit: 250 bytes)"
                  charLimit={250} testid="lo-copy-search-terms" />
              )}
              {(result.a_plus_headline || result.a_plus_subhead) && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {result.a_plus_headline && (
                    <CopyBlock title="A+ headline" value={result.a_plus_headline}
                      hint="Hero module" charLimit={60} testid="lo-copy-aplus-headline" />
                  )}
                  {result.a_plus_subhead && (
                    <CopyBlock title="A+ sub-headline" value={result.a_plus_subhead}
                      hint="Hero module · sub-head" charLimit={140} testid="lo-copy-aplus-subhead" />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {rateLimited && <RateLimited />}
    </div>
  );
}
